import React, { useState } from 'react';
import { ResultTable, ChartBlock, PieBlock } from '../shared/blocks/ResultBlocks';

// Inline-editable variant of the shared ResultTable: click a cell, type, blur/Enter
// saves the whole table through onSave (numbers stay numbers when they still parse).
const ts = {
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '8px' },
    th: { padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid var(--border)', textAlign: 'left', fontWeight: 600, fontSize: '12px', color: 'var(--text-secondary)' },
    td: { padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: 'var(--text-primary)', cursor: 'text' },
    cellInput: { width: '100%', minWidth: '60px', padding: '3px 6px', border: '1px solid var(--accent)', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' },
};

function EditableTable({ rows, onSave }) {
    const [editing, setEditing] = useState(null); // { r, c } | null
    const [draft, setDraft] = useState('');
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const headers = Object.keys(rows[0]);

    const startCell = (r, c) => {
        setEditing({ r, c });
        setDraft(rows[r]?.[c] == null ? '' : String(rows[r][c]));
    };
    const commitCell = () => {
        if (!editing) return;
        const { r, c } = editing;
        setEditing(null);
        const original = rows[r]?.[c];
        if (draft === (original == null ? '' : String(original))) return;
        const asNumber = draft.trim() === '' ? NaN : Number(draft);
        const value = typeof original === 'number' && Number.isFinite(asNumber) ? asNumber : draft;
        onSave(rows.map((row, i) => (i === r ? { ...row, [c]: value } : row)));
    };

    return (
        <table style={ts.table}>
            <thead>
                <tr>{headers.map((h) => <th key={h} style={ts.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
                {rows.map((row, r) => (
                    <tr key={r}>
                        {headers.map((c) => (
                            <td
                                key={c}
                                style={ts.td}
                                title="Click to edit"
                                onClick={() => { if (!editing || editing.r !== r || editing.c !== c) startCell(r, c); }}
                            >
                                {editing && editing.r === r && editing.c === c ? (
                                    <input
                                        style={ts.cellInput}
                                        value={draft}
                                        autoFocus
                                        onChange={(e) => setDraft(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') commitCell(); if (e.key === 'Escape') setEditing(null); }}
                                        onBlur={commitCell}
                                    />
                                ) : (
                                    row[c] == null ? '' : String(row[c])
                                )}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

const st = {
    vizGap: { marginTop: '14px' },
    card: { border: '1px solid #d3ddea', borderTop: '3px solid var(--accent)', borderRadius: '14px', background: '#fff', padding: '12px 16px 14px', boxShadow: '0 1px 4px rgba(19,40,72,0.08)', display: 'flex', flexDirection: 'column', gap: '8px', position: 'relative' },
    // Dims the content AND swallows clicks — mid-regeneration edits would be
    // overwritten by the incoming block anyway.
    pendingOverlay: { position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.65)', borderRadius: '14px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', zIndex: 2 },
    pendingSpinner: { width: '22px', height: '22px', borderRadius: '50%', border: '3px solid var(--border)', borderTopColor: 'var(--accent)', animation: 'mcpspin 0.7s linear infinite' },
    pendingLabel: { fontSize: '12.5px', fontWeight: 600, color: 'var(--text-secondary)' },
    head: { display: 'flex', alignItems: 'flex-start', gap: '10px' },
    headTitle: { flex: 1, minWidth: 0 },
    headline: { fontSize: '14.5px', fontWeight: 700, color: 'var(--text-primary)', margin: 0, cursor: 'text', lineHeight: 1.4 },
    headlineInput: { fontSize: '14.5px', fontWeight: 700, color: 'var(--text-primary)', border: '1px solid var(--accent)', borderRadius: '6px', padding: '4px 8px', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
    callouts: { fontSize: '13px', lineHeight: 1.7, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', margin: 0, cursor: 'text' },
    calloutsEmpty: { fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic', cursor: 'text' },
    calloutsInput: { fontSize: '13px', lineHeight: 1.6, color: 'var(--text-primary)', border: '1px solid var(--accent)', borderRadius: '6px', padding: '6px 8px', fontFamily: 'inherit', outline: 'none', width: '100%', minHeight: '64px', resize: 'vertical' },
    controls: { display: 'flex', gap: '4px', alignItems: 'center', flex: '0 0 auto' },
    editPromptBtn: { display: 'inline-flex', alignItems: 'center', gap: '5px', border: '1px solid var(--accent)', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: '8px', padding: '5px 10px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
    ctlBtn: (disabled) => ({ border: '1px solid var(--border)', background: '#fff', cursor: disabled ? 'not-allowed' : 'pointer', color: disabled ? '#cbd5e1' : 'var(--text-secondary)', fontSize: '13px', padding: '5px 8px', borderRadius: '8px', lineHeight: 1, fontFamily: 'inherit' }),
};

export default function ArtifactBlock({ block, readOnly = false, pending, isFirst, isLast, onEdit, onMove, onDelete, onEditPrompt }) {
    const [editing, setEditing] = useState(null); // null | 'headline' | 'callouts'
    const [draft, setDraft] = useState('');

    const startEdit = (field) => { setEditing(field); setDraft(block[field] || ''); };
    const commitEdit = () => {
        if (editing && draft !== (block[editing] || '')) onEdit(block.id, { [editing]: draft });
        setEditing(null);
    };

    // Server normalizes to `visuals`, but a block streamed from an older server
    // (or replayed from a cached SSE frame) may still carry a single `viz`.
    const visuals = Array.isArray(block.visuals) && block.visuals.length > 0
        ? block.visuals
        : block.viz ? [block.viz] : [];
    return (
        <div style={st.card}>
            {pending && (
                <div style={st.pendingOverlay} aria-live="polite">
                    <span style={st.pendingSpinner} aria-hidden="true" />
                    <span style={st.pendingLabel}>Regenerating…</span>
                </div>
            )}
            <div style={st.head}>
                <div style={st.headTitle}>
                    {editing === 'headline' && !readOnly ? (
                        <input style={st.headlineInput} value={draft} autoFocus onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditing(null); }} onBlur={commitEdit} />
                    ) : (
                        <h3 style={readOnly ? { ...st.headline, cursor: 'default' } : st.headline} title={readOnly ? undefined : 'Click to edit'} onClick={readOnly ? undefined : () => startEdit('headline')}>{block.headline || 'Untitled'}</h3>
                    )}
                </div>
                {/* Always visible: the old hover-revealed icon strip was routinely
                    missed, and ✎ is the block's primary affordance — it gets a
                    label while the secondary actions stay compact. */}
                {!readOnly && (
                    <div style={st.controls}>
                        <button type="button" style={st.editPromptBtn} onClick={() => onEditPrompt(block)} title="Load this block's prompt into the chat box — edit it and send to regenerate the block">✎ Edit prompt</button>
                        <button type="button" style={st.ctlBtn(isFirst)} disabled={isFirst} onClick={() => onMove(block.id, -1)} title="Move up" aria-label="Move up">↑</button>
                        <button type="button" style={st.ctlBtn(isLast)} disabled={isLast} onClick={() => onMove(block.id, 1)} title="Move down" aria-label="Move down">↓</button>
                        <button type="button" style={st.ctlBtn(false)} onClick={() => onDelete(block.id)} title="Delete block" aria-label="Delete block">🗑</button>
                    </div>
                )}
            </div>
            {visuals.map((viz, i) => (
                <div key={i} style={i > 0 ? st.vizGap : undefined}>
                    {viz?.type === 'table' && Array.isArray(viz.table) && (
                        readOnly
                            ? <ResultTable rows={viz.table} />
                            // visualIndex tells the server WHICH visual the edit
                            // targets — without it every edit would hit the first.
                            : <EditableTable rows={viz.table} onSave={(table) => onEdit(block.id, { table, visualIndex: i })} />
                    )}
                    {(viz?.type === 'bar' || viz?.type === 'line') && viz.Chart_data && <ChartBlock chartData={viz.Chart_data} />}
                    {viz?.type === 'pie' && viz.Chart_data && <PieBlock chartData={viz.Chart_data} />}
                </div>
            ))}
            {editing === 'callouts' && !readOnly ? (
                <textarea style={st.calloutsInput} value={draft} autoFocus onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setEditing(null); }} onBlur={commitEdit} />
            ) : block.callouts ? (
                <p style={readOnly ? { ...st.callouts, cursor: 'default' } : st.callouts} title={readOnly ? undefined : 'Click to edit'} onClick={readOnly ? undefined : () => startEdit('callouts')}>{block.callouts}</p>
            ) : readOnly ? null : (
                <span style={st.calloutsEmpty} onClick={() => startEdit('callouts')}>Add callouts…</span>
            )}
        </div>
    );
}
